import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, BellRing, Download, Loader2, Mail, RefreshCw, Search, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';
import { usePermissions } from '../../../context/PermissionsContext';
import { createEventGuestsApi, lifecycleCode } from '../../../lib/eventGuestsApi';
import { createEventContactsApi, isEventContactConflict } from '../../../lib/eventContactsApi';
import { errorText } from '../../../lib/apiErrors';
import {
    BLOCKED_BEFORE_START, CLOSE_REASON_OPTIONS, CONVERTED_KEYS, START_FIRST_TEXT, columnLabel, columnsFrom, deriveColumn,
    effectOf, moveTitle, phaseOf,
} from '../../../lib/eventJourney';
import { formatClock, formatLocalDate } from '../../../lib/eventWorkspace';
import {
    isFinalOutcome, logEventEmail, newOperationId, offerToCloseFollowUps, recordBulkJourneyMove, safeOperationId,
} from '../../../lib/eventOutreachActions';
import ContactEmailComposer from '../../ContactEmailComposer';
import EventStageActionDialog from '../EventStageActionDialog';
import BulkEventInviteDialog from '../BulkEventInviteDialog';
import GuestsLifecycleBar from './GuestsLifecycleBar';
import GuestsMetricStrip from './GuestsMetricStrip';
import GuestsBoard from './GuestsBoard';
import GuestsList from './GuestsList';
import GuestsCheckIn from './GuestsCheckIn';
import GuestsBulkBar from './GuestsBulkBar';
import GuestDrawer from './GuestDrawer';
import AddGuestsDialog from './AddGuestsDialog';
import RescheduleEventDialog from './RescheduleEventDialog';
import RescheduleGuestDialog from './RescheduleGuestDialog';
import EndEventDialog from './EndEventDialog';
import { StartEventDialog, ReopenEventDialog } from './LifecycleDialogs';
import { downloadCsv, guestsCsv } from './guestsCsv';

// A deep link may carry these into the board request (an Overview team row, the Events "Needs attention" strip).
const REQUEST_FILTERS = ['owner_id', 'follow_up', 'stage', 'rsvp_status'];
const VIEWS = [['board', 'Board'], ['list', 'List'], ['checkin', 'Check-in']];
const FOLLOW_UPS = [['', 'Any'], ['due', 'Due'], ['overdue', 'Overdue']];

const linkedFilters = (initial) => Object.fromEntries(REQUEST_FILTERS.filter((key) => initial?.[key]).map((key) => [key, String(initial[key])]));
const conflictMessage = (error) => `Updated by ${error?.response?.data?.detail?.last_changed_by?.name || 'another user'} · Refresh`;
const when = (event) => `${formatLocalDate(event?.date)}${event?.start_time ? ` · ${formatClock(event.start_time)}` : ''}`;

/**
 * The Guests tab (#1013): ONE pipeline for everyone on the event, in three views of the same rows (Board /
 * List / Check-in), under a lifecycle bar (Start / End / Reopen / Reschedule) and a metric strip. It
 * replaces the Outreach, Attendees and Follow-up tabs. A guest's column is derived on the server from facts
 * (invite sent, RSVP, attendance, follow-up stage); the browser only asks for moves and shows what comes back.
 *
 * Props: `event` (the event row, with `phase`, `started_at`, `ended_at`, `started_by_name`…), `readOnly`
 * (no permission to add activities), `canManageLifecycle` (`events:update`: shows the four lifecycle buttons),
 * `initialFilters` / `initialContactId` (deep links), `onEventChanged` (called after Start, End, Reopen,
 * Reschedule and any change to the guest list, so the page can reload the event).
 */
const localToday = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

export default function EventGuestsTab({
    eventId, event, getAuthHeader, readOnly = false, canManageLifecycle = false, initialFilters, initialContactId, onEventChanged,
}) {
    const { user } = useAuth();
    const { hasEffectivePermission } = usePermissions();
    const canCreateContact = hasEffectivePermission('contacts:create');
    const canAssignOwner = hasEffectivePermission('contacts:assign_owner');
    const api = useMemo(() => createEventGuestsApi(getAuthHeader), [getAuthHeader]);
    const contactsApi = useMemo(() => createEventContactsApi(getAuthHeader), [getAuthHeader]);

    const phase = phaseOf(event);
    const [view, setView] = useState('board');
    const [filters, setFilters] = useState(() => ({ owner_id: '', follow_up: '', stage: '', rsvp_status: '', ...linkedFilters(initialFilters) }));
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [spotlight, setSpotlight] = useState(initialFilters?.column || null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState([]);
    const [flash, setFlash] = useState([]);
    const [optimistic, setOptimistic] = useState({});
    const [pending, setPending] = useState([]);
    const [busy, setBusy] = useState(false);
    const [scrollTarget, setScrollTarget] = useState(null);

    const [drawerId, setDrawerId] = useState(null);
    const [outOfView, setOutOfView] = useState(null);
    const [history, setHistory] = useState({ items: [], loading: false, error: '' });
    const [moveAction, setMoveAction] = useState(null);
    const [lifecycle, setLifecycle] = useState(null);
    const [preview, setPreview] = useState({ data: null, loading: false, error: '' });
    const [reschedEvent, setReschedEvent] = useState({ open: false, error: '' });
    const [reschedGuests, setReschedGuests] = useState(null);
    const [addMode, setAddMode] = useState(null);
    const [composerCard, setComposerCard] = useState(null);
    const [mail, setMail] = useState(null);

    useEffect(() => { const timer = setTimeout(() => setDebounced(search.trim()), 250); return () => clearTimeout(timer); }, [search]);
    const linkedKey = JSON.stringify(linkedFilters(initialFilters));
    useEffect(() => {
        const linked = JSON.parse(linkedKey);
        if (Object.keys(linked).length) setFilters((current) => ({ ...current, ...linked }));
    }, [linkedKey]);
    useEffect(() => { if (initialFilters?.column) setSpotlight(initialFilters.column); }, [initialFilters?.column]);

    const requestParams = useMemo(() => ({
        search: debounced || undefined,
        owner_id: filters.owner_id && filters.owner_id !== 'mine' ? filters.owner_id : undefined,
        mine: filters.owner_id === 'mine' || undefined,
        follow_up: filters.follow_up || undefined, stage: filters.stage || undefined, rsvp_status: filters.rsvp_status || undefined,
    }), [debounced, filters]);

    const boardRequest = useRef(0);
    const loadBoard = useCallback(async ({ silent = false } = {}) => {
        const token = ++boardRequest.current;               // a slower, older response never overwrites a newer one
        if (!silent) setLoading(true);
        setError(null);
        try {
            const board = await api.getBoard(eventId, requestParams);
            if (token !== boardRequest.current) return;
            setData(board);
            setOptimistic({});
        } catch (err) {
            if (token === boardRequest.current) setError(errorText(err, 'Failed to load Guests'));
        } finally {
            if (token === boardRequest.current) setLoading(false);
        }
    }, [api, eventId, requestParams]);
    useEffect(() => { loadBoard(); }, [loadBoard]);
    useEffect(() => {
        if (data?.filters?.default_mine && !filters.owner_id) setFilters((current) => ({ ...current, owner_id: 'mine' }));
    }, [data, filters.owner_id]);

    // Open the board where the work is: the start before the event, Going on the day, Attended after.
    const scrolledFor = useRef(null);
    useEffect(() => {
        if (!data || scrolledFor.current === phase) return;
        scrolledFor.current = phase;
        setScrollTarget({ key: phase === 'before' ? null : phase === 'event_day' ? 'going' : 'attended', nonce: Date.now() });
    }, [data, phase]);

    const columns = useMemo(() => columnsFrom(data?.columns), [data]);
    const columnOf = useCallback((card) => deriveColumn(card, columns), [columns]);
    const cards = useMemo(() => (data?.cards || []).map((card) => (optimistic[card.contact_id] ? { ...card, ...optimistic[card.contact_id] } : card)), [data, optimistic]);
    const summary = data?.summary;
    const selectedCards = cards.filter((card) => selected.includes(card.contact_id));
    const countIn = (key) => cards.filter((card) => columnOf(card) === key).length;
    // The open drawer's Journey is re-read with the board, so a move made from the drawer shows up in it at once.
    const drawerIdRef = useRef(null);
    const reloadHistoryRef = useRef(null);
    const refreshAll = useCallback(async () => {
        await loadBoard({ silent: true });
        if (drawerIdRef.current) reloadHistoryRef.current?.(drawerIdRef.current);
        onEventChanged?.();
    }, [loadBoard, onEventChanged]);
    const flashFor = (ids) => { setFlash(ids); setTimeout(() => setFlash([]), 4200); };

    // ---- moves -------------------------------------------------------------------------------------
    // Every by-hand move asks first (the dropdown, a drop, Mark invited, the bulk bar): the form saves the
    // evidence activity and the transition together, and Cancel changes nothing.
    const requestMove = useCallback((targets, key, extra = {}) => {
        if (readOnly || !key) return;
        if (phase === 'before' && BLOCKED_BEFORE_START.includes(key)) { toast.info(START_FIRST_TEXT); return; }
        const movable = targets.filter((card) => columnOf(card) !== key);
        if (!movable.length) return;
        setMoveAction({ cards: movable, target: key, closeReason: extra.closeReason });
    }, [columnOf, phase, readOnly]);

    const finalMove = (key) => key === 'closed' || CONVERTED_KEYS.includes(key);

    const submitMove = async ({ channel, note, followUpDate, reason }) => {
        if (!moveAction) return;
        const { cards: targets, target } = moveAction;
        const label = columnLabel(target, columns);
        setBusy(true);
        try {
            const operationId = newOperationId(targets.length > 1 ? `bulk-${target}` : `move-${target}`, eventId);
            const { results } = await recordBulkJourneyMove({
                getAuthHeader, eventId, contacts: targets, target, label, closeReason: reason, channel, note, followUpDate, operationId,
            });
            const failed = results.filter((result) => result.status === 'rejected');
            const moved = results.filter((result) => result.status === 'fulfilled').map((result) => result.contact.contact_id);
            setMoveAction(null);
            setSelected([]);
            if (failed.length) {
                const conflict = failed.find((result) => isEventContactConflict(result.reason));
                toast.error(conflict ? conflictMessage(conflict.reason) : `${failed.length} of ${results.length} not moved: ${errorText(failed[0].reason, 'Could not move')}`);
            } else {
                toast.success(`${moved.length === 1 ? targets[0].name : `${moved.length} guests`} → ${label}`);
            }
            if (moved.length) flashFor(moved);
            if (moved.length && finalMove(target)) {
                const closed = await offerToCloseFollowUps({ getAuthHeader, eventId, contactIds: moved });
                if (closed.failed) toast.error('Moved, but some follow-ups could not be closed');
                else if (closed.closed) toast.success('Open follow-ups closed');
            }
            await refreshAll();
        } finally {
            setBusy(false);
        }
    };

    // ---- check-in switches -------------------------------------------------------------------------
    const toggleCheckIn = useCallback(async (card, on) => {
        if (phase === 'before') { toast.info('Start the event to open check-in'); return; }
        // Undoing a check-in clears the attendance fact only (#1013 audit F3): a walk-in or an Invited guest
        // never said yes, so "back to Going" would invent an RSVP. After End, a guest who was Going goes
        // back to No-show, everyone else to pending.
        const undoTarget = phase === 'after' && card.rsvp_status === 'confirmed' ? 'no_show' : 'pending';
        const target = on ? 'attended' : undoTarget;
        const patch = { attendance_status: target };
        patch.journey_column = on ? 'attended' : target === 'no_show' ? 'no_show'
            : card.rsvp_status === 'confirmed' ? 'going' : card.invited_at ? 'invited' : 'to_invite';
        setOptimistic((current) => ({ ...current, [card.contact_id]: patch }));
        setPending((current) => current.concat(card.contact_id));
        try {
            await contactsApi.transitionContact(eventId, card.contact_id, {
                ...(on ? { journey_target: 'attended' } : { attendance_status: target }),
                reason: 'check_in', expected_state_version: card.state_version ?? 0,
                operation_id: safeOperationId('checkin', eventId, card.contact_id, target, card.state_version ?? 0),
            });
            toast.success(on ? `${card.name} checked in · moved to Attended` : `Check-in undone · ${card.name} ${target === 'no_show' ? 'is a No-show' : 'is no longer checked in'}`);
            await refreshAll();
        } catch (err) {
            setOptimistic((current) => { const next = { ...current }; delete next[card.contact_id]; return next; });
            toast.error(isEventContactConflict(err) ? conflictMessage(err) : errorText(err, 'Could not update the check-in'));
            await loadBoard({ silent: true });
        } finally {
            setPending((current) => current.filter((id) => id !== card.contact_id));
        }
    }, [contactsApi, eventId, loadBoard, phase, refreshAll]);

    // ---- emails ------------------------------------------------------------------------------------
    const firstName = '{{FirstName}}';
    const openMail = (contacts, options) => setMail({ contacts, eventId, eventName: event?.name, ...options });
    const mailFor = (kind, targets) => {
        const name = event?.name || 'the event';
        const day = formatLocalDate(event?.date);
        if (kind === 'nudge') return openMail(targets, { noun: 'nudge', doneVerb: 'Nudged', initialSubject: `Can you join us? ${name}`, initialBody: `Hi ${firstName},\n\nJust checking in: can you make ${name} on ${day}? A quick yes or no helps us plan.\n\n` });
        if (kind === 'remind') return openMail(targets, { noun: 'reminder', doneVerb: 'Reminded', initialSubject: `Reminder: ${name}, ${day}`, initialBody: `Hi ${firstName},\n\nA quick reminder that ${name} is on ${day}${event?.start_time ? ` at ${formatClock(event.start_time)}` : ''}. We look forward to seeing you.\n\n` });
        return openMail(targets, {
            noun: 'thank-you', doneVerb: 'Thanked', initialSubject: `Thank you for joining ${name}`, initialBody: `Hi ${firstName},\n\nThank you for coming to ${name}. It was great to have you.\n\n`,
            after: moveToFollowingUp,
        });
    };

    // Sending a thank-you moves the card to Following up (1013-18): each guest's fresh row version is read first.
    async function moveToFollowingUp({ results }) {
        const stage = columns.find((column) => column.key === 'following_up') ? 'following_up' : null;
        if (!stage) return;
        const operationId = newOperationId('thank-you', eventId);
        const sent = results.filter((result) => result.ok).map((result) => result.contact);
        const outcomes = await Promise.allSettled(sent.map(async (contact) => {
            const fresh = (await contactsApi.getContact(eventId, contact.contact_id))?.attendee;
            return contactsApi.transitionContact(eventId, contact.contact_id, {
                outreach_stage: stage, reason: 'thank_you_sent', expected_state_version: fresh?.state_version ?? contact.state_version ?? 0, operation_id: operationId,
            });
        }));
        if (outcomes.some((outcome) => outcome.status === 'rejected')) toast.warning('Thank-you sent, but some cards could not move to Following up');
        else if (sent.length) flashFor(sent.map((contact) => contact.contact_id));
    }

    const handleInviteSent = async ({ subject } = {}) => {
        const card = composerCard;
        if (!card) return;
        try {
            await logEventEmail({ getAuthHeader, eventId, contactId: card.contact_id, subject });
        } catch (err) {
            toast.warning(errorText(err, 'Email sent, but the event invitation could not be recorded'));
        }
        setComposerCard(null);
        flashFor([card.contact_id]);
        await refreshAll();
    };

    const onAction = (card, action) => {
        const { kind } = action;
        if (kind === 'invite') setComposerCard(card);
        else if (kind === 'nudge' || kind === 'remind' || kind === 'thanks') mailFor(kind, [card]);
        else if (kind === 'checkin') toggleCheckIn(card, true);
        else if (kind === 'resched') setReschedGuests([card]);
        else if (kind === 'deal') window.location.assign(card.deal_id ? `/deals/${encodeURIComponent(card.deal_id)}` : `/contacts/${encodeURIComponent(card.contact_id)}`);
        else if (kind.startsWith('move:')) requestMove([card], kind.slice(5), { closeReason: action.closeReason });
    };

    // ---- lifecycle ---------------------------------------------------------------------------------
    const openLifecycle = async (kind) => {
        setLifecycle(kind);
        if (kind !== 'end') return;
        setPreview({ data: null, loading: true, error: '' });
        try {
            setPreview({ data: await api.getEndPreview(eventId), loading: false, error: '' });
        } catch (err) {
            setPreview({ data: null, loading: false, error: errorText(err, 'The counts could not be loaded.') });
        }
    };
    // One operation id per attempt (#1013 audit F9): a retry after a lost response replays on the server
    // instead of answering 409 (End would otherwise be sent twice under two ids). It is dropped on success
    // and when the server says the page was stale.
    const attemptIds = useRef({});
    const attemptId = (key, kind) => {
        if (!attemptIds.current[key]) attemptIds.current[key] = newOperationId(kind, eventId);
        return attemptIds.current[key];
    };
    const confirmLifecycle = async () => {
        const kind = lifecycle;
        if (!kind) return;
        setBusy(true);
        try {
            const operationId = attemptId(kind, kind);
            if (kind === 'start') {
                await api.startEvent(eventId, operationId);
                toast.success('Event started · check-in is open. Nobody moved: Going guests stay in Going until they check in.');
            } else if (kind === 'end') {
                const res = await api.endEvent(eventId, operationId);
                toast.success(`Event ended · ${res?.moved_to_no_show ?? 0} Going guests without a check-in moved to No-show`);
            } else {
                const res = await api.reopenEvent(eventId, operationId);
                toast.success(`Check-in reopened · ${res?.reverted ?? 0} guests are back in Going`);
            }
            delete attemptIds.current[kind];
            setLifecycle(null);
            await refreshAll();
        } catch (err) {
            // 409 `event_already_started`, `event_already_ended`, `event_not_started`, `event_not_ended`: the page was stale.
            toast.error(errorText(err, 'Could not change the event'));
            if (lifecycleCode(err)) { delete attemptIds.current[kind]; setLifecycle(null); await refreshAll(); }
        } finally {
            setBusy(false);
        }
    };

    const submitRescheduleEvent = async ({ date, start_time, end_time, notify, reconfirm }) => {
        const recipients = cards.filter((card) => ['going', 'invited'].includes(columnOf(card)) && card.email);
        setBusy(true);
        setReschedEvent((current) => ({ ...current, error: '' }));
        try {
            const res = await api.rescheduleEvent(eventId, {
                date, start_time, end_time, reconfirm, expected_updated_at: event?.updated_at, operation_id: attemptId(`reschedule:${date}:${start_time}:${end_time}:${reconfirm}`, 'reschedule'),
            });
            attemptIds.current = Object.fromEntries(Object.entries(attemptIds.current).filter(([key]) => !key.startsWith('reschedule:')));
            setReschedEvent({ open: false, error: '' });
            const newDate = formatLocalDate(res?.event?.date || date);
            toast.success(`Event moved to ${newDate}${res?.reconfirmed ? ` · ${res.reconfirmed} Going guests asked to reconfirm` : ''}`);
            await refreshAll();
            // D5: the new-date email goes from the user's own mailbox, through the composer.
            if (notify && recipients.length) {
                openMail(recipients, {
                    noun: 'update', doneVerb: 'Emailed', initialSubject: `New date for ${event?.name || 'the event'}`,
                    initialBody: `Hi ${firstName},\n\n${event?.name || 'The event'} has moved to ${newDate}${start_time ? ` at ${formatClock(start_time)}` : ''}. ${reconfirm ? 'Please let us know that you can still make it.' : 'Your place is kept.'}\n\n`,
                });
            }
        } catch (err) {
            setReschedEvent({ open: true, error: errorText(err, 'Could not reschedule the event') });
            if (lifecycleCode(err)) onEventChanged?.();
        } finally {
            setBusy(false);
        }
    };

    const listUpcoming = useCallback(async () => {
        const res = await api.listUpcomingEvents({ date_from: localToday(), page_size: 50 });
        return Array.isArray(res) ? res : res?.data || [];
    }, [api]);

    const submitRescheduleGuests = async ({ target, sendInvite }) => {
        const targets = reschedGuests || [];
        setBusy(true);
        try {
            const done = [];
            const failures = [];
            // One at a time: each is a full audited transition on two events.
            for (const card of targets) {
                try {
                    await api.rescheduleGuest(eventId, card.contact_id, {
                        target_event_id: target.event_id, expected_state_version: card.state_version ?? 0,
                        operation_id: safeOperationId('resched-guest', eventId, card.contact_id, target.event_id),
                    });
                    done.push(card);
                } catch (err) {
                    failures.push({ card, err });
                }
            }
            setReschedGuests(null);
            setSelected([]);
            const where = `${target.name} · ${formatLocalDate(target.date)}`;
            if (failures.length) {
                const conflict = failures.find((item) => isEventContactConflict(item.err));
                toast.error(conflict ? conflictMessage(conflict.err) : `${failures.length} of ${targets.length} not rescheduled: ${errorText(failures[0].err, 'Could not reschedule')}`);
            }
            if (done.length) {
                toast.success(`${done.length === 1 ? done[0].name : `${done.length} guests`} added to ${where} as To invite`);
                flashFor(done.map((card) => card.contact_id));
            }
            await refreshAll();
            // D7: "Send the invite now" opens the composer for the TARGET event; its send moves them to Invited there.
            if (sendInvite && done.length) setMail({ contacts: done.filter((card) => card.email), eventId: target.event_id, eventName: target.name });
        } finally {
            setBusy(false);
        }
    };

    // ---- bulk --------------------------------------------------------------------------------------
    const bulkInvite = () => {
        const targets = selectedCards.filter((card) => columnOf(card) === 'to_invite');
        if (!targets.length) { toast.info('Invites go to guests in To invite · none selected'); return; }
        setMail({ contacts: targets, eventId, eventName: event?.name });
    };
    const removeCards = async (targets) => {
        if (readOnly || !targets.length) return;
        setBusy(true);
        try {
            const results = await Promise.allSettled(targets.map((card) => contactsApi.removeAttendee(eventId, card.event_contact_id)));
            const failed = results.filter((result) => result.status === 'rejected');
            if (failed.length) toast.error(`${failed.length} of ${results.length} not removed: ${errorText(failed[0].reason, 'Could not remove')}`);
            else toast.success(`${targets.length === 1 ? targets[0].name : `${targets.length} guests`} removed from the event`);
            setSelected([]);
            if (targets.some((card) => card.contact_id === drawerId)) closeDrawer();
            await refreshAll();
        } finally {
            setBusy(false);
        }
    };
    const closeOutUnconfirmed = async (targets) => {
        setBusy(true);
        try {
            const operationId = newOperationId('close-out', eventId);
            const results = await Promise.allSettled(targets.map((card) => contactsApi.transitionContact(eventId, card.contact_id, {
                journey_target: 'closed', close_reason: columnOf(card) === 'invited' ? 'no_reply' : 'never_invited', reason: 'event_close_out',
                expected_state_version: card.state_version ?? 0, operation_id: operationId,
            })));
            const failed = results.filter((result) => result.status === 'rejected').length;
            if (failed) toast.error(`${failed} of ${targets.length} could not be closed`);
            else toast.success(`${targets.length} closed · No reply / Never invited`);
            await refreshAll();
        } finally {
            setBusy(false);
        }
    };

    // ---- drawer ------------------------------------------------------------------------------------
    const loadHistory = useCallback(async (contactId, { silent = false } = {}) => {
        if (!silent) setHistory({ items: [], loading: true, error: '' });
        try {
            const res = await api.getHistory(eventId, { contact_id: contactId, limit: 100 });
            setHistory({ items: res?.items || [], loading: false, error: '' });
        } catch (err) {
            setHistory({ items: [], loading: false, error: errorText(err, 'Could not load the journey') });
        }
    }, [api, eventId]);
    reloadHistoryRef.current = (contactId) => loadHistory(contactId, { silent: true });
    const openDrawer = useCallback((card) => { setOutOfView(null); setDrawerId(card.contact_id); loadHistory(card.contact_id); }, [loadHistory]);
    const closeDrawer = useCallback(() => { setDrawerId(null); setOutOfView(null); }, []);

    // A deep link (`initialContactId`, from Contact Detail or the Activity tab) opens that guest's drawer once
    // the board has loaded. A guest the board does not list (an assigned-only viewer, a filter) opens from the row.
    const openedRef = useRef(null);
    useEffect(() => {
        if (!initialContactId || !data || openedRef.current === initialContactId) return;
        openedRef.current = initialContactId;
        const card = (data.cards || []).find((item) => item.contact_id === initialContactId);
        if (card) { openDrawer(card); return; }
        contactsApi.getContact(eventId, initialContactId).then((res) => {
            const row = res?.attendee || {};
            setOutOfView({ contact_id: initialContactId, event_contact_id: row.attendee_id, name: row.contact_name || 'Contact', rsvp_status: row.rsvp_status || 'no_response', attendance_status: row.attendance_status, outreach_stage: row.outreach_stage, invited_at: row.invited_at, close_reason: row.close_reason, state_version: row.state_version ?? 0, journey_column: row.journey_column, source: row.source });
            setDrawerId(initialContactId);
            loadHistory(initialContactId);
        }).catch((err) => toast.error(err?.response?.status === 404 ? "That contact isn't on this event." : errorText(err, 'Could not open that contact')));
    }, [data, initialContactId, contactsApi, eventId, openDrawer, loadHistory]);
    drawerIdRef.current = drawerId;
    const drawerCard = drawerId ? (cards.find((card) => card.contact_id === drawerId) || outOfView) : null;

    // ---- render ------------------------------------------------------------------------------------
    const toggleCard = (card) => setSelected((current) => (current.includes(card.contact_id) ? current.filter((id) => id !== card.contact_id) : [...current, card.contact_id]));
    const toggleAll = (rows) => setSelected((current) => (rows.every((card) => current.includes(card.contact_id)) ? [] : rows.map((card) => card.contact_id)));
    const guestLabels = useMemo(() => Object.fromEntries(cards.map((card) => [card.contact_id, columnLabel(columnOf(card), columns)])), [cards, columnOf, columns]);
    const eventLine = `${event?.name || 'This event'} · ${when(event)}`;
    const unconfirmed = cards.filter((card) => ['to_invite', 'invited'].includes(columnOf(card)));
    const overdueNudges = cards.filter((card) => columnOf(card) === 'invited' && card.follow_up_state === 'overdue');
    const ownerOptions = [['', 'Everyone'], ['mine', 'Mine'], ...(data?.owner_options || []).map((owner) => [owner.id, owner.name])];

    if (loading && !data) return <div className="flex items-center gap-2 p-6 text-sm text-stone-500"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Loading Guests…</div>;
    if (error && !data) {
        return (
            <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-700">
                <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" aria-hidden="true" />{error}</div>
                <button type="button" className="mt-3 inline-flex items-center gap-1 rounded border border-red-300 px-3 py-1.5" onClick={() => loadBoard()}><RefreshCw className="h-3 w-3" aria-hidden="true" />Retry</button>
            </div>
        );
    }

    const banner = 'flex flex-wrap items-center gap-2.5 rounded-[10px] border px-3.5 py-2.5 text-[13px]';
    const link = 'font-semibold underline underline-offset-2 disabled:opacity-50';
    const selectCls = 'h-8 rounded-lg border border-stone-200 bg-white px-2 text-[13px] font-medium';
    const moveFrom = moveAction ? columnOf(moveAction.cards[0]) : null;
    const moveTarget = moveAction?.target;
    return (
        <section className="space-y-3" data-testid="event-guests-tab" data-phase={phase}>
            <GuestsLifecycleBar
                event={event} phase={phase} summary={summary} view={view} canManage={canManageLifecycle}
                onStart={() => openLifecycle('start')} onEnd={() => openLifecycle('end')} onReopen={() => openLifecycle('reopen')}
                onReschedule={() => setReschedEvent({ open: true, error: '' })} onOpenCheckIn={() => setView('checkin')}
            />
            <GuestsMetricStrip
                summary={summary} capacity={data?.capacity} event={event} phase={phase} spotlight={spotlight}
                onSpotlight={(key) => { setSpotlight(key); if (key) { setView('board'); setScrollTarget({ key, nonce: Date.now() }); } }}
            />

            {data?.has_more && (
                <div data-testid="guests-banner-truncated" className={`${banner} border-amber-200 bg-amber-50 text-amber-900`}>
                    <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="flex-1">Showing the first {(data.cards || []).length} guests only, and the numbers above count just those. Narrow the search or the filters to see the rest.</span>
                </div>
            )}
            {phase === 'after' && unconfirmed.length > 0 && (
                <div data-testid="guests-banner-unconfirmed" className={`${banner} border-amber-200 bg-amber-50 text-amber-900`}>
                    <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="flex-1">{unconfirmed.length} guests never confirmed and are still in To invite or Invited. Reschedule them to a future event, or close them for this one.</span>
                    <button type="button" className={link} disabled={readOnly || busy} onClick={() => setReschedGuests(unconfirmed)}>Reschedule them</button>
                    <button type="button" className={link} disabled={readOnly || busy} onClick={() => closeOutUnconfirmed(unconfirmed)}>Close them all</button>
                </div>
            )}
            {phase === 'before' && overdueNudges.length > 0 && (
                <div data-testid="guests-banner-overdue" className={`${banner} border-red-200 bg-red-50 text-red-900`}>
                    <BellRing className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="flex-1">{overdueNudges.length} invited guests are overdue for a nudge.</span>
                    <button type="button" className={link} onClick={() => setFilters((current) => ({ ...current, follow_up: 'overdue' }))}>Show overdue</button>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2" data-testid="guests-toolbar">
                <div className="flex items-center gap-0.5 rounded-lg bg-stone-100 p-0.5" role="group" aria-label="View">
                    {VIEWS.map(([key, label]) => (
                        <button key={key} type="button" aria-pressed={view === key} data-testid={`guests-view-${key}`} onClick={() => { setView(key); setSelected([]); }}
                            className={`inline-flex h-7 items-center rounded-md px-2.5 text-[12.5px] font-medium ${view === key ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-600'}`}>{label}</button>
                    ))}
                </div>
                {view !== 'checkin' && (
                    <label className="relative">
                        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400" aria-hidden="true" />
                        <input aria-label="Search guests" placeholder="Search guests" value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 w-52 rounded-lg border border-stone-200 bg-white pl-8 pr-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </label>
                )}
                <select aria-label="Filter by owner" className={selectCls} value={filters.owner_id} onChange={(e) => setFilters((current) => ({ ...current, owner_id: e.target.value }))}>
                    {ownerOptions.map(([value, label]) => <option key={value || 'all'} value={value}>{`Owner: ${label}`}</option>)}
                </select>
                <select aria-label="Follow-up filter" className={selectCls} value={filters.follow_up} onChange={(e) => setFilters((current) => ({ ...current, follow_up: e.target.value }))}>
                    {FOLLOW_UPS.map(([value, label]) => <option key={value || 'any'} value={value}>{`Follow-up: ${label}`}</option>)}
                </select>
                {spotlight && (
                    <button type="button" data-testid="guests-clear-spotlight" onClick={() => setSpotlight(null)} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-stone-300 bg-stone-100 px-2.5 text-[13px] font-medium">
                        Spotlight: {columnLabel(spotlight, columns)}<X className="h-3 w-3" aria-hidden="true" />
                    </button>
                )}
                <span className="flex-1" />
                {view === 'list' && (
                    <button type="button" data-testid="guests-export-csv" disabled={cards.length === 0} onClick={() => downloadCsv(guestsCsv(cards, { columns, columnOf, phase }), `${(event?.name || 'event').replace(/[^A-Za-z0-9]+/g, '-')}-guests.csv`)}
                        className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 text-[13px] font-medium hover:bg-stone-50 disabled:opacity-50"><Download className="h-3.5 w-3.5" aria-hidden="true" />Export CSV</button>
                )}
                <button type="button" data-testid="guests-add" disabled={readOnly} onClick={() => setAddMode('pick')} className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-stone-900 px-3 text-[13px] font-semibold text-white hover:bg-stone-800 disabled:opacity-50"><UserPlus className="h-3.5 w-3.5" aria-hidden="true" />Add guests</button>
            </div>

            {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
            {view === 'board' && (
                <GuestsBoard
                    columns={columns} cards={cards} columnOf={columnOf} phase={phase} event={event} selectedIds={selected} spotlight={spotlight} flashIds={flash}
                    readOnly={readOnly || busy} scrollTarget={scrollTarget} onOpen={openDrawer} onToggle={toggleCard} onAction={onAction} onRequestMove={requestMove}
                />
            )}
            {view === 'list' && (
                <GuestsList columns={columns} cards={cards} columnOf={columnOf} phase={phase} selectedIds={selected} onToggle={toggleCard} onToggleAll={toggleAll}
                    onOpen={openDrawer} onRequestMove={requestMove} readOnly={readOnly || busy} />
            )}
            {view === 'checkin' && (
                <GuestsCheckIn
                    cards={cards} columnOf={columnOf} phase={phase} summary={summary} search={search} onSearch={setSearch} pendingIds={pending} readOnly={readOnly}
                    lockText={`Check-in opens when the event starts (${formatLocalDate(event?.date)}${event?.start_time ? `, ${formatClock(event.start_time)}` : ''}, or press Start event). This is the list staff will see at the door.`}
                    onToggle={toggleCheckIn} onAddWalkIn={() => setAddMode('walk-in')}
                />
            )}

            <GuestsBulkBar count={moveAction ? 0 : selected.length} columns={columns} busy={busy} readOnly={readOnly} onInvite={bulkInvite}
                onMove={(key) => requestMove(selectedCards, key)} onRemove={() => removeCards(selectedCards)} onClear={() => setSelected([])} />

            {drawerCard && (
                <GuestDrawer
                    card={drawerCard} columnKey={columnOf(drawerCard)} columns={columns} phase={phase} history={history} timeZone={event?.timezone} readOnly={readOnly}
                    onClose={closeDrawer} onRequestMove={requestMove} onEmail={setComposerCard} onRemove={(card) => removeCards([card])} onRetryHistory={() => loadHistory(drawerCard.contact_id)}
                />
            )}

            <EventStageActionDialog
                open={Boolean(moveAction)}
                mode={moveTarget === 'invited' ? 'invite' : 'stage'}
                title={moveTarget && moveTarget !== 'invited' ? `${moveTitle(moveTarget, columns)}${moveAction.cards.length > 1 ? ` · ${moveAction.cards.length} guests` : ''}` : undefined}
                stageLabel={moveTarget ? columnLabel(moveTarget, columns) : ''}
                count={moveAction?.cards.length || 1}
                effect={moveTarget ? effectOf(moveFrom, moveTarget, columns) : null}
                reasons={moveTarget === 'closed' ? CLOSE_REASON_OPTIONS : null}
                defaultReason={moveAction?.closeReason || ''}
                finalOutcome={Boolean(moveTarget) && (finalMove(moveTarget) || isFinalOutcome({ stage: moveTarget, stages: data?.workflow?.stages }))}
                busy={busy}
                onClose={() => setMoveAction(null)}
                onSubmit={submitMove}
            />

            <StartEventDialog open={lifecycle === 'start'} busy={busy} onClose={() => setLifecycle(null)} onConfirm={confirmLifecycle}
                eventLine={eventLine} goingCount={countIn('going')} startClock={event?.start_time ? formatClock(event.start_time) : ''} />
            <EndEventDialog open={lifecycle === 'end'} busy={busy} onClose={() => setLifecycle(null)} onConfirm={confirmLifecycle}
                preview={preview.data} loading={preview.loading} error={preview.error} eventLine={eventLine} endClock="23:59" />
            <ReopenEventDialog open={lifecycle === 'reopen'} busy={busy} onClose={() => setLifecycle(null)} onConfirm={confirmLifecycle} />
            <RescheduleEventDialog open={reschedEvent.open} busy={busy} error={reschedEvent.error} event={event} onClose={() => setReschedEvent({ open: false, error: '' })} onSubmit={submitRescheduleEvent} />
            <RescheduleGuestDialog open={Boolean(reschedGuests)} busy={busy} guests={reschedGuests || []} currentEventId={eventId} listEvents={listUpcoming} onClose={() => setReschedGuests(null)} onSubmit={submitRescheduleGuests} />
            <AddGuestsDialog
                open={Boolean(addMode)} initialMode={addMode || 'pick'} onClose={() => setAddMode(null)} api={api} eventId={eventId} eventLine={eventLine} phase={phase}
                guestLabels={guestLabels} canCreateContact={canCreateContact} canAssignOwner={canAssignOwner} currentUserName={user?.name}
                onAdded={async () => { await refreshAll(); }}
            />
            {composerCard && (
                <ContactEmailComposer
                    open onOpenChange={(open) => { if (!open) setComposerCard(null); }} contact={composerCard}
                    eventOptions={[{ event_id: eventId, name: event?.name || 'This event' }]} onSent={handleInviteSent}
                />
            )}
            <BulkEventInviteDialog
                open={Boolean(mail)} onClose={() => setMail(null)} getAuthHeader={getAuthHeader}
                eventId={mail?.eventId || eventId} eventName={mail?.eventName} contacts={mail?.contacts || []}
                initialSubject={mail?.initialSubject} initialBody={mail?.initialBody} noun={mail?.noun} doneVerb={mail?.doneVerb}
                onDone={async (result) => { const after = mail?.after; setMail(null); setSelected([]); await after?.(result); await refreshAll(); }}
            />
        </section>
    );
}
